import { Outlet, NavLink as RouterNavLink, useLocation } from 'react-router'
import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { CopilotKit } from '@copilotkit/react-core'
import {
    ActionIcon,
    AppShell,
    Box,
    Burger,
    Button,
    Center,
    Flex,
    Group,
    Loader,
    MantineProvider,
    Menu,
    Modal,
    NavLink,
    ScrollArea,
    Textarea,
    Tooltip,
    useComputedColorScheme,
    useMantineColorScheme,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { theme, cssVariablesResolver } from './theme'
import '@mantine/core/styles.layer.css'
import 'mantine-datatable/styles.layer.css'
import '@copilotkit/react-core/v2/styles.css'

import { useConfig } from './config'
import { ResumableRuntimeAgent } from '@/features/thread/ResumableRuntimeAgent'
import type { Message } from '@copilotkit/shared'
import { AudienceServiceProvider } from '@/features/audience/AudienceServiceProvider'
import { UserProfile } from '@/components/UserProfile'
import {
    IconBroadcast,
    IconCode,
    IconHelp,
    IconHome,
    IconMessages,
    IconMoon,
    IconPlus,
    IconSend,
    IconSun,
    IconUsers,
} from '@tabler/icons-react'
import { ThreadServiceProvider } from '@/features/thread/ThreadServiceProvider'
import { useGetAccessToken } from '@/hooks/useGetAuthClaims'
import { CtaButton } from '@/components/CtaButton'
import { useCreateNewAudience } from '@/features/thread/useCreateNewAudience'

function ColorSchemeToggle() {
    const { setColorScheme } = useMantineColorScheme()
    const computedColorScheme = useComputedColorScheme('light', {
        getInitialValueInEffect: true,
    })
    const isDark = computedColorScheme === 'dark'

    // CopilotKit v2's dark theme keys off a `.dark` ancestor (see
    // node_modules/@copilotkit/react-core/src/v2/styles/globals.css). Mirror
    // Mantine's computed scheme onto <html> so the chat surface flips too.
    useEffect(() => {
        document.documentElement.classList.toggle('dark', isDark)
    }, [isDark])

    return (
        <Tooltip
            label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
            <ActionIcon
                variant='subtle'
                color='gray'
                size='lg'
                aria-label='Toggle color scheme'
                onClick={() => setColorScheme(isDark ? 'light' : 'dark')}
            >
                {isDark ? <IconSun size={20} /> : <IconMoon size={20} />}
            </ActionIcon>
        </Tooltip>
    )
}

export function RootLayout() {
    const { isAuthenticated, isLoading } = useAuth0()
    const [isInspectorEnabled, setIsInspectorEnabled] = useState(false)
    const { createNewAudience } = useCreateNewAudience()

    const config = useConfig()

    const { data: accessToken } = useGetAccessToken()

    const copilotkitHeaders = useMemo(() => {
        const headers: Record<string, string> = {}
        if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`
        }
        return headers
    }, [accessToken])

    // Live ref so the (stable) agent reads current auth headers on each
    // reconnect without being re-created when the token refreshes — a new agent
    // instance would re-fire CopilotChat's connect effect (keyed on `agent`).
    const headersRef = useRef(copilotkitHeaders)
    headersRef.current = copilotkitHeaders

    // PROTOTYPE: a self-managed agent whose reconnect replays durable thread
    // state from /message_snapshot (so a paused HITL renders live), instead of
    // the runtime's ephemeral in-memory replay. See ResumableRuntimeAgent.
    const resumableAgent = useMemo(
        () =>
            new ResumableRuntimeAgent({
                runtimeUrl: config.copilotkitRuntimeUrl,
                // Distinct local id so the runtime-discovered agent doesn't
                // overwrite this instance; runs route to the runtime agent.
                agentId: 'audience_builder_agent_ui',
                runtimeAgentId: 'audience_builder_agent',
                loadSnapshot: async (threadId, opts) => {
                    const headers = headersRef.current
                    const q = opts?.waitForRun ? '?wait=1' : ''
                    const [msgRes, stateRes] = await Promise.all([
                        fetch(
                            `${config.aguiServiceUrl}/message_snapshot/${threadId}${q}`,
                            { headers }
                        ),
                        fetch(
                            `${config.aguiServiceUrl}/state_snapshot/${threadId}${q}`,
                            { headers }
                        ),
                    ])
                    // 404 = no session yet (brand-new thread) → empty history
                    // is correct. Any other non-OK is a real failure; throw so
                    // connect surfaces it instead of silently rendering an empty
                    // thread. (State stays best-effort.)
                    if (!msgRes.ok && msgRes.status !== 404) {
                        throw new Error(
                            `message_snapshot ${threadId} failed: ${msgRes.status}`
                        )
                    }
                    const messages = msgRes.ok
                        ? ((await msgRes.json()) as { messages: Message[] })
                              .messages
                        : []
                    const state = stateRes.ok
                        ? (
                              (await stateRes.json()) as {
                                  snapshot?: Record<string, unknown>
                              }
                          ).snapshot
                        : undefined
                    return { messages, state }
                },
            }),
        [config.copilotkitRuntimeUrl, config.aguiServiceUrl]
    )

    const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] =
        useDisclosure()
    const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true)

    // Close only the mobile overlay on navigation; the desktop sidebar stays
    // as the user left it (collapse is manual, via the header burger) so the
    // primary nav doesn't vanish on every click.
    const location = useLocation()
    useEffect(() => {
        closeMobile()
    }, [location, closeMobile])

    const navItems = [
        {
            to: '/',
            label: 'Dashboard',
            icon: IconHome,
            active: location.pathname === '/',
        },
        {
            to: '/thread',
            label: 'Threads',
            icon: IconMessages,
            active: location.pathname.startsWith('/thread'),
        },
        {
            to: '/audiences',
            label: 'Audiences',
            icon: IconUsers,
            active: location.pathname.startsWith('/audiences'),
        },
        {
            to: '/distributions',
            label: 'Distributions',
            icon: IconSend,
            active: location.pathname.startsWith('/distributions'),
        },
        {
            to: '/destinations',
            label: 'Destinations',
            icon: IconBroadcast,
            active: location.pathname.startsWith('/destinations'),
        },
    ]

    // manage the modal state
    const [opened, { open, close }] = useDisclosure(false)

    const loaderFallback = (
        <Center h='100%'>
            <Loader />
        </Center>
    )

    let mainContent
    if (isLoading || (isAuthenticated && !accessToken)) {
        mainContent = loaderFallback
    } else if (!isAuthenticated) {
        mainContent = <Outlet />
    } else {
        mainContent = (
            <CopilotKit
                runtimeUrl={config.copilotkitRuntimeUrl}
                agent='audience_builder_agent_ui'
                selfManagedAgents={{
                    audience_builder_agent_ui: resumableAgent,
                }}
                headers={copilotkitHeaders}
                publicLicenseKey='ck_pub_35403a330c524a0ed72a324ed037c5ca'
                showDevConsole={true}
                enableInspector={isInspectorEnabled}
            >
                <Outlet />
            </CopilotKit>
        )
    }

    return (
        <MantineProvider
            theme={theme}
            defaultColorScheme='auto'
            cssVariablesResolver={cssVariablesResolver}
        >
            <ThreadServiceProvider apiUrl={config.aguiServiceUrl}>
                <AudienceServiceProvider apiUrl={config.audienceServiceUrl}>
                    <AppShell
                        h='100%'
                        padding='md'
                        header={{ height: 60 }}
                        navbar={{
                            width: 200,
                            breakpoint: 'sm',
                            collapsed: {
                                mobile: !mobileOpened,
                                desktop: !desktopOpened,
                            },
                        }}
                    >
                        <AppShell.Header>
                            <Group h='100%' px='md' w='100%'>
                                <Burger
                                    opened={mobileOpened}
                                    onClick={toggleMobile}
                                    hiddenFrom='sm'
                                    size='sm'
                                    aria-label={
                                        mobileOpened
                                            ? 'Close navigation'
                                            : 'Open navigation'
                                    }
                                />
                                <Burger
                                    opened={desktopOpened}
                                    onClick={toggleDesktop}
                                    visibleFrom='sm'
                                    size='sm'
                                    aria-label={
                                        desktopOpened
                                            ? 'Collapse navigation'
                                            : 'Expand navigation'
                                    }
                                />

                                {isAuthenticated && (
                                    <>
                                        <Flex
                                            style={{
                                                flexGrow: 1,
                                            }}
                                        />
                                        <ColorSchemeToggle />
                                        <UserProfile
                                            menuItems={[
                                                <Menu.Item
                                                    key='help'
                                                    leftSection={
                                                        <IconHelp size={14} />
                                                    }
                                                    onClick={open}
                                                >
                                                    Help
                                                </Menu.Item>,
                                                <Menu.Item
                                                    key='devconsole'
                                                    leftSection={
                                                        <IconCode size={14} />
                                                    }
                                                    onClick={() =>
                                                        setIsInspectorEnabled(
                                                            !isInspectorEnabled
                                                        )
                                                    }
                                                >
                                                    Dev Console
                                                </Menu.Item>,
                                                <Menu.Divider key='divider' />,
                                            ]}
                                        />
                                    </>
                                )}
                            </Group>
                        </AppShell.Header>
                        <AppShell.Navbar>
                            <AppShell.Section>
                                <Box p='sm'>
                                    <CtaButton
                                        leftSection={<IconPlus size={20} />}
                                        onClick={createNewAudience}
                                        fullWidth
                                    >
                                        New Audience
                                    </CtaButton>
                                </Box>
                            </AppShell.Section>
                            <AppShell.Section grow component={ScrollArea}>
                                {navItems.map(
                                    ({ to, label, icon: Icon, active }) => (
                                        <NavLink
                                            key={to}
                                            leftSection={<Icon />}
                                            component={RouterNavLink}
                                            to={to}
                                            label={label}
                                            active={active}
                                        />
                                    )
                                )}
                            </AppShell.Section>
                            <AppShell.Section>
                                <NavLink
                                    onClick={open}
                                    leftSection={<IconHelp />}
                                    label='Help'
                                />
                            </AppShell.Section>
                        </AppShell.Navbar>
                        <AppShell.Main h={'100%'}>{mainContent}</AppShell.Main>
                    </AppShell>
                    <Modal
                        opened={opened}
                        onClose={close}
                        title={
                            <Group>
                                <IconHelp /> Help Center
                            </Group>
                        }
                        overlayProps={{
                            backgroundOpacity: 0.55,
                            blur: 3,
                        }}
                        centered
                    >
                        <Textarea
                            aria-label='Your question or issue'
                            description='Ask a question or describe what you need help with. Our team (or AI assistant) will get back to you shortly.'
                            placeholder='Type your question or issue here'
                            autosize
                            minRows={4}
                            data-autofocus
                        />
                        <Group justify='flex-end' mt='md'>
                            <Button variant='default' onClick={close}>
                                Cancel
                            </Button>
                            <Button>Submit</Button>
                        </Group>
                    </Modal>
                </AudienceServiceProvider>
            </ThreadServiceProvider>
        </MantineProvider>
    )
}
